import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useColorScheme } from '@mui/material/styles';

/**
 * The three states, in the order the control cycles them.
 *
 * `system` is first and is the default, so a visitor who never touches this keeps
 * whatever their operating system asked for. The cycle returns to it rather than
 * stranding someone in a manual choice they cannot undo.
 */
type Mode = 'system' | 'light' | 'dark';

/**
 * Successor in the cycle.
 *
 * A lookup rather than modulo arithmetic over an ordered tuple: under
 * `noUncheckedIndexedAccess` an indexed read is `Mode | undefined`, so the
 * arithmetic version needs a fallback that can never actually happen. This is both
 * total and easier to read.
 */
const NEXT: Record<Mode, Mode> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

const LABEL: Record<Mode, string> = {
  system: 'following your system setting',
  light: 'light',
  dark: 'dark',
};

const ICON: Record<Mode, typeof LightModeIcon> = {
  system: SettingsBrightnessIcon,
  light: LightModeIcon,
  dark: DarkModeIcon,
};

/**
 * Cycles the colour scheme between system, light and dark.
 *
 * The site already followed `prefers-color-scheme` through the theme's
 * `colorSchemes`; this adds the override, which matters for anyone whose OS
 * preference does not match the room they are sitting in. MUI persists the choice
 * to `localStorage`, so it survives a reload.
 *
 * The accessible name states the *current* scheme rather than the action, because
 * a control that announces "switch to dark" gives a screen reader user no way to
 * discover which scheme they are already in.
 */
export function ColorSchemeToggle() {
  const { mode, setMode } = useColorScheme();

  // Undefined until the provider has resolved on the client. Rendering a disabled
  // placeholder of the same size keeps the toolbar from reflowing on first paint.
  if (!mode) {
    return (
      <IconButton disabled aria-hidden size="small">
        <SettingsBrightnessIcon fontSize="small" />
      </IconButton>
    );
  }

  const current: Mode = mode;
  const next = NEXT[current];
  const Icon = ICON[current];

  return (
    <Tooltip title={`Colour scheme: ${LABEL[current]}`}>
      <IconButton
        onClick={() => setMode(next)}
        size="small"
        aria-label={`Colour scheme: ${LABEL[current]}. Change to ${LABEL[next]}.`}
        sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
      >
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
